/**
 * Print sets control (Phase 65 / BLU-594 PR2, tier-2 footer).
 *
 * Colour chips that flip a colour between the light and dark print set by
 * toggling `settings.darkColours` in the shared buffer — the flip is instant
 * (visual + matrix suffix), and PERSISTS through the same Save as the code
 * tables (a dirty buffer surfaces its save affordance). "Auto-detect dark
 * colours" seeds from a conservative heuristic for the operator to review.
 * Read-only for non-admins (mirrors RipRoutingSection's admin gate).
 */
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Wand2 } from 'lucide-react';
import { nameListIncludes } from '@threadheads/shared';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ColourDot } from './ColourDot';
import { detectDarkColours } from '../lib/dark-colour-matcher';
import type { ProductSyncSettingsBuffer } from '../hooks/useProductSyncSettingsBuffer';

interface PrintSetsControlProps {
  colours: string[];
  buffer: ProductSyncSettingsBuffer;
  admin: boolean;
}

export function PrintSetsControl({ colours, buffer, admin }: PrintSetsControlProps) {
  const { t } = useTranslation('productSync');

  if (colours.length === 0) {
    return <p className="text-xs text-muted-foreground">{t('tier2.noColours')}</p>;
  }

  const handleToggle = (name: string) => {
    if (!admin) return;
    const willBeDark = !nameListIncludes(buffer.settings.darkColours, name);
    buffer.toggleDarkColour(name, willBeDark);
    toast(willBeDark ? t('tier2.movedToDark', { name }) : t('tier2.movedToLight', { name }));
  };

  const handleAutoDetect = () => {
    if (!admin) return;
    const candidates = detectDarkColours(colours);
    const added = buffer.addDarkColours(candidates);
    if (added > 0) {
      toast.success(t('tier2.autoDetectResult', { count: added }));
    } else {
      toast(t('tier2.autoDetectNone'));
    }
  };

  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span className="text-xs font-semibold">{t('tier2.printSets')}</span>
        <span className="text-xs text-muted-foreground">{t('tier2.printSetsHint')}</span>
        {admin && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs"
            onClick={handleAutoDetect}
          >
            <Wand2 className="h-3.5 w-3.5" aria-hidden="true" />
            {t('tier2.autoDetect')}
          </Button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {colours.map((name) => {
          const isDark = nameListIncludes(buffer.settings.darkColours, name);
          return (
            <button
              key={name}
              type="button"
              disabled={!admin}
              aria-pressed={isDark}
              onClick={() => handleToggle(name)}
              className={cn(
                'inline-flex h-8 items-center gap-2 rounded-full border pl-2.5 pr-3 text-xs font-semibold transition-colors duration-200 motion-reduce:transition-none',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                isDark
                  ? 'border-[#2A2733] bg-[#2A2733] text-white'
                  : 'border-border bg-background text-foreground',
                admin ? (isDark ? 'hover:ring-2 hover:ring-threadheads-purple/30' : 'hover:border-threadheads-purple/60') : 'cursor-default opacity-90',
              )}
            >
              <ColourDot name={name} />
              <span>{name}</span>
              <span
                className={cn(
                  'rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide',
                  isDark ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground',
                )}
              >
                {isDark ? t('tier2.tagDark') : t('tier2.tagLight')}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
